import { Vector2, type ShaderMaterial } from "three";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { CHUNKS } from "./chunks.ts";
import { NEAR_FADE } from "./near-fade.ts";
import { createSurfaceMaterial, lodFadeBand, PALETTE_DEFAULTS, surfaceVariant } from "./surface.ts";

export { NEAR_FADE, NEAR_FADE_FLOOR, NEAR_FADE_LEAF_WIND, nearFadeEligible } from "./near-fade.ts";

/** Vértice do sumiço perto da câmera: profundidade no espaço do olho e, com o piso, a altura acima da base e se é copa. */
export const NEAR_FADE_VERTEX = CHUNKS.nearFade.vertex;

/**
 * Fragmento do sumiço: descarta o pixel perto da câmera conforme o limiar Bayer da sua célula 4 × 4
 * na tela (uma divisão e uma comparação); com o piso, a casca só acima dele. Vem depois de todas as
 * derivadas.
 */
export const NEAR_FADE_FRAGMENT = CHUNKS.nearFade.fragment;

/**
 * Troca de LOD e sumiço na distância (`USE_FAR_FADE`): a instância entra entre `uFarFade.x` e `.y` e
 * sai entre `.z` e `.w` (m, no chão da câmera até a base dela) por pontilhado na tela, inteira no
 * tamanho; a entrada do LOD de longe completa a saída do de perto, pixel a pixel.
 */
export const FAR_FADE_VERTEX = CHUNKS.farFade.vertex;

/** Fragmento da troca de LOD: o descarte pelo limiar Bayer, depois de todas as derivadas. */
export const FAR_FADE_FRAGMENT = CHUNKS.farFade.fragment;

/** Força do vento da folhagem (o vento de cada vértice vem do atributo `wind`). */
const WIND_STRENGTH = 0.18;

/**
 * Material único dos recursos (folhagem, troncos, rochas), drops, estruturas e o item na mão: o
 * material de superfície com cor por vértice, instâncias e vento (F51). `outline` põe a máscara do
 * contorno (itens e estruturas; a folhagem e o cenário ficam sem).
 */
export function createFoliageMaterial(atmosphere: AtmosphereUniforms, options: { outline?: boolean } = {}): ShaderMaterial {
  return createSurfaceMaterial({ atmosphere, name: "folhagem", vertexColors: true, wind: WIND_STRENGTH, outline: options.outline === true });
}

/**
 * Variante do material com paletas por instância (F42): a geometria traz uma cor por paleta
 * (`color`, `color1`, `color2`) e o atributo de instância `palette` (0, 1 ou 2) escolhe qual. Uma
 * paleta a mais não custa draw call. Divide os uniforms (atmosfera, vento, contorno) com o original.
 */
export function withPalettes(material: ShaderMaterial): ShaderMaterial {
  const out = surfaceVariant(material, { name: `${material.name}-paletas`, defines: { USE_PALETTES: "" }, shared: true });
  Object.assign(out.defaultAttributeValues, PALETTE_DEFAULTS);
  return out;
}

/**
 * Variante do material que some em pontilhado perto da câmera (`NEAR_FADE`), para o que o olho pode
 * atravessar: recursos (árvores, rochas) e POIs. Em vez de o plano near fatiar a malha e mostrar o
 * oco, ela se desfaz antes. Com `floor` (os recursos, instanciados), a casca só some a essa altura
 * acima da base da instância ou mais (`NEAR_FADE_FLOOR`) e a copa, em qualquer altura; os POIs,
 * fundidos no espaço do mundo, não têm base e somem em qualquer altura. Divide os uniforms do
 * original (atmosfera, vento) e compõe com `withPalettes`. É cenário: sem a máscara do contorno.
 * Drops, estruturas e o item na mão usam o original.
 */
export function withNearFade(material: ShaderMaterial, floor?: number): ShaderMaterial {
  const gated = floor !== undefined;
  return surfaceVariant(material, {
    name: `${material.name}-perto`,
    defines: { USE_NEAR_FADE: "", ...(gated && { USE_NEAR_FADE_FLOOR: "" }) },
    uniforms: {
      uNearFade: { value: new Vector2(NEAR_FADE.gone, NEAR_FADE.solid) },
      ...(gated && { uNearFadeFloor: { value: floor } }),
      uOutline: { value: 0 },
    },
  });
}

/**
 * Variante do material que some na distância por pontilhado (`FAR_FADE_VERTEX`), sem pipocar nem
 * encolher: os enfeites do chão (F44) entre 0,85 R e R; nos recursos, a silhueta de longe também entra
 * (`fadeIn`) na faixa em que a malha de perto sai. As faixas (m) ficam no uniform `uFarFade`
 * (`lodFadeBand`), que a camada atualiza com a distância de visão; variantes com faixas diferentes
 * dividem o programa. Divide os uniforms do original e compõe com `withNearFade` e `withPalettes`. É
 * cenário: sem a máscara do contorno.
 */
export function withFarFade(material: ShaderMaterial, start: number, end: number, fadeIn?: readonly [number, number]): ShaderMaterial {
  return surfaceVariant(material, {
    name: `${material.name}-borda`,
    defines: { USE_FAR_FADE: "" },
    uniforms: { uFarFade: { value: lodFadeBand(start, end, fadeIn) }, uOutline: { value: 0 } },
  });
}
